/**
 * Handles i18n translation logic for the message box page. Updates static text elements
 * on the page by manipulating the DOM based on the user-selected language using LinguiJS.
 *
 * - Loads and applies translations dynamically.
 * - Updates DOM elements with the appropriate localized text.
 *
 * @author Akash Salvi <akash.salvi@helpshift.com>
 * @created April 28, 2025
 * @module pages/internationalization/messageBox
 */

import dom from "dom";
import {msg, t} from "@lingui/core/macro";
import {updateFooter} from "pages/internationalization/internationalizationLogic";

const MESSAGE_BOX_CONTENT_MAP = {
  "#password-reset-link-sent": {
    title: msg`Password reset link has been sent`,
    text: msg`An email with a link to reset your password has been sent to your email address.`
  },
  "#invalid-registration-token": {
    text: msg`Invalid user registration token.`
  },
  "#link-expired": {
    text: msg`This link has expired.`
  },
  "#link-invalid": {
    text: msg`This link is Invalid.`
  },
  "#link-no-longer-valid": {
    text: msg`This invitation is no longer valid.`
  },
  "#site-with-this-email-exists": {
    text: msg`You have already created a site using this email address.`
  },
  "#link-invalid-request-new-link": {
    text: msg`This link is invalid, please request a new link via the Forgot Password widget on the login page`
  },
  "#invalid-request": {
    text: msg`Invalid Request.`
  },
  "#invalid-details": {
    text: msg`The details you have entered are invalid. Please try again.`
  },
  "#login-process-issue": {
    text: msg`Something went wrong in the login process.`
  }
};

const messageBoxTitle = dom.one("#message-box-title");
const messageBoxText = dom.one("#message-box-text");

const accountNotFoundTitle = dom.one("#account-not-found-title");
const accountNotFoundText = dom.one("#account-not-found-text");
const contactAdminText = dom.one("#contact-admin-text");

const accountExists = dom.one("#account-exists");
const providerIdEl = dom.one("#provider-id");

/**
 * Updates all Message Box UI elements and their message content using the appropriate
 * translations based on the user's selected language.
 *
 * NOTE: The backend creates the Message Box HTML, so we can’t include translations
 * directly when the page is rendered.
 * Therefore, we use DOM manipulation to replace the static text with the
 * user’s preferred language translation after the page loads.
 */
export const updateMessageBoxContent = () => {
  updateFooter();

  // Content for message box when an account with the specified email already exists
  if (accountExists) {
    const accountExistMsg = dom.one("#account-exists-msg");
    const stepToFollow = dom.one("#step-to-follow");
    const email = dom.one("#email").getDOM().innerText;
    const emailMarkupText = `<strong id="email">${email}</strong>`;

    accountExistMsg.getDOM().innerHTML = t`A user account for ${emailMarkupText} has already been created.`;
    stepToFollow.getDOM().innerText = t`Please login or click the Forgot Password link in case you have forgotten your password.`;

    dom.one("#login-option").getDOM().innerText = t`Login`;
    dom.one("#forget-password-option").getDOM().innerText = t`Forgot password`;

    // Content for message box when no account is found
  } else if (accountNotFoundTitle) {
    const userEmail = dom.one("#account-user-email").getDOM().innerText;
    const hostName = dom.one("#host-name").getDOM().innerText;
    const userEmailMarkupText = `<strong id="account-user-email">${userEmail}</strong>`;
    const hostNameMarkupText = `<span id="host-name">${hostName}</span>`;

    accountNotFoundTitle.getDOM().innerText = t`Account Not Found`;
    accountNotFoundText.getDOM().innerHTML = t`It seems like ${userEmailMarkupText} does not have access to this site.`;
    contactAdminText.getDOM().innerHTML = t`If this is a mistake, please contact the administrator of ${hostNameMarkupText}`;

    // Content for message box when the access-token record does not match
  } else if (providerIdEl) {
    const providerId = providerIdEl.getDOM().innerText;
    const userEmail = dom.one("#user-email").getDOM().innerText;
    const providerIdMarkupText = `<span id="provider-id">${providerId}</span>`;
    const userEmailMarkupText = `<span id="user-email">${userEmail}</span>`;

    messageBoxText.getDOM().innerHTML = t`A ${providerIdMarkupText} login for ${userEmailMarkupText} already exists but we have a different access-token on record.`;

    // Content for message box when the element ID exists in the MESSAGE_BOX_CONTENT_MAP data
  } else {
    Object.keys(MESSAGE_BOX_CONTENT_MAP).some((id) => {
      const messageBoxEl = dom.one(id);

      if (messageBoxEl) {
        const messageDetails = MESSAGE_BOX_CONTENT_MAP[id];

        if (messageDetails.title) {
          messageBoxTitle.getDOM().innerText = t(messageDetails.title);
        }

        messageBoxText.getDOM().innerText = t(messageDetails.text);
      }
    });
  }
};
